import { Check } from 'lucide-preact'
import type { LandingPageDesignRoundDto } from '@/types/landingPage'

/**
 * A discovery round once answered: the questions as Webster asked them, with the answers left in
 * place — the chips that were picked, what was typed. Read-only; the direction it produced is edited
 * in Context & Settings.
 */
export function AnsweredDesignRound({ round }: { round: LandingPageDesignRoundDto }) {
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-4">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                A couple of quick things before I build
            </p>

            {round.Questions.map((q, i) => {
                // Matched by question text, the same identity the form answers by.
                const answer = round.Answers.find(a => a.Question === q.Question)
                const picked = answer?.SelectedOptions ?? []
                // An open question's joined Answer is its text; a dashboard older than the API sent
                // only that.
                const typed = answer?.Text ?? answer?.Answer
                return (
                    <div key={`${q.Question}-${i}`} className="space-y-2">
                        <p className="text-sm text-slate-700 dark:text-slate-200">{q.Question}</p>

                        {q.Options.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {q.Options.map((option) => {
                                    const isPicked = picked.includes(option)
                                    return (
                                        <span
                                            key={option}
                                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs ${
                                                isPicked
                                                    ? 'border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                                    : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-400 dark:text-slate-500'
                                            }`}
                                        >
                                            {isPicked && <Check className="w-3 h-3" aria-hidden="true" />}
                                            {option}
                                            {isPicked && <span className="sr-only">(picked)</span>}
                                            {q.Recommended === option && (
                                                <span className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                                    suggested
                                                </span>
                                            )}
                                        </span>
                                    )
                                })}
                            </div>
                        ) : typed ? (
                            <p className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
                                {typed}
                            </p>
                        ) : null}
                    </div>
                )
            })}

            {round.Note && (
                <div className="space-y-2">
                    <p className="text-xs text-slate-500 dark:text-slate-400">Anything else I should know?</p>
                    <p className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
                        {round.Note}
                    </p>
                </div>
            )}
        </div>
    )
}
